import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/data';

const categoryImages: Record<string, string> = {
  recommend: 'https://readdy.ai/api/search-image?query=Warm%20orange%20and%20amber%20cinematic%20movie%20collage%20with%20soft%20glowing%20highlights%2C%20popular%20entertainment%20atmosphere%2C%20abstract%20film%20elements%2C%20modern%20minimalist%20design%20with%20warm%20tones%2C%20no%20text&width=800&height=450&seq=cat-recommend-hero&orientation=landscape',
  movie: 'https://readdy.ai/api/search-image?query=Cinematic%20film%20theater%20atmosphere%20with%20warm%20projector%20light%20beams%2C%20abstract%20movie%20elements%20floating%20in%20golden%20light%2C%20soft%20bokeh%20background%2C%20elegant%20entertainment%20aesthetic%2C%20no%20text&width=800&height=450&seq=cat-movie-hero&orientation=landscape',
  tv: 'https://readdy.ai/api/search-image?query=Modern%20living%20room%20with%20large%20TV%20screen%20displaying%20warm%20abstract%20visuals%2C%20cozy%20ambient%20lighting%20with%20orange%20and%20golden%20tones%2C%20comfortable%20home%20entertainment%20atmosphere%2C%20no%20text&width=800&height=450&seq=cat-tv-hero&orientation=landscape',
  variety: 'https://readdy.ai/api/search-image?query=Vibrant%20stage%20lighting%20with%20warm%20colorful%20spotlights%2C%20entertainment%20show%20atmosphere%2C%20abstract%20celebration%20elements%20with%20orange%20and%20golden%20hues%2C%20dynamic%20composition%2C%20no%20text&width=800&height=450&seq=cat-variety-hero&orientation=landscape',
  anime: 'https://readdy.ai/api/search-image?query=Artistic%20anime%20style%20abstract%20background%20with%20warm%20orange%20and%20soft%20pastel%20colors%2C%20gentle%20gradient%20with%20floating%20geometric%20shapes%2C%20dreamy%20atmosphere%2C%20creative%20and%20playful%20composition%2C%20no%20characters%20or%20text&width=800&height=450&seq=cat-anime-hero&orientation=landscape',
  documentary: 'https://readdy.ai/api/search-image?query=Natural%20landscape%20with%20warm%20golden%20hour%20sunlight%2C%20abstract%20documentary%20film%20elements%2C%20soft%20atmospheric%20composition%20with%20earthy%20warm%20tones%2C%20cinematic%20nature%20photography%20style%2C%20no%20text&width=800&height=450&seq=cat-doc-hero&orientation=landscape',
};

const articleCardImages: Record<string, string> = {
  'article-3': 'https://readdy.ai/api/search-image?query=Cinematic%20film%20reel%20and%20movie%20posters%20in%20warm%20ambient%20lighting%2C%20abstract%20summer%20vibes%20with%20golden%20hour%20tones%2C%20theater%20curtains%20in%20soft%20focus%2C%20entertainment%20atmosphere%2C%20no%20text%20or%20people&width=800&height=450&seq=summer-movie-article-3&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Dramatic%20cinematic%20scene%20with%20warm%20lighting%20and%20soft%20bokeh%20background%2C%20abstract%20representation%20of%20Chinese%20TV%20drama%2C%20elegant%20composition%20with%20golden%20and%20amber%20tones%2C%20storytelling%20atmosphere%2C%20no%20people%20or%20text&width=800&height=450&seq=drama-guide-article-5&orientation=landscape',
  'article-7': 'https://readdy.ai/api/search-image?query=Ultra%20high%20definition%20cinematic%20scene%20with%20crystal%20clear%20details%2C%20warm%20orange%20and%20golden%20lighting%2C%20abstract%20technology%20and%20entertainment%20elements%2C%20premium%20visual%20quality%20aesthetic%2C%20no%20text&width=800&height=450&seq=4k-zone-article-7&orientation=landscape',
  'article-8': 'https://readdy.ai/api/search-image?query=Artistic%20anime%20style%20abstract%20composition%20with%20warm%20orange%20and%20soft%20pastel%20tones%2C%20floating%20geometric%20shapes%20and%20gentle%20light%20rays%2C%20creative%20and%20dreamy%20atmosphere%2C%20no%20characters%20or%20text&width=800&height=450&seq=anime-guide-article-8&orientation=landscape',
  'article-10': 'https://readdy.ai/api/search-image?query=Warm%20golden%20hour%20natural%20landscape%20with%20soft%20atmospheric%20lighting%2C%20documentary%20film%20aesthetic%20with%20rich%20earthy%20tones%2C%20cinematic%20composition%20with%20depth%2C%20nature%20photography%20style%2C%20no%20text&width=800&height=450&seq=documentary-guide-article-10&orientation=landscape',
  'article-11': 'https://readdy.ai/api/search-image?query=Vibrant%20stage%20with%20warm%20colorful%20spotlights%20and%20confetti%2C%20entertainment%20show%20atmosphere%2C%20abstract%20celebration%20elements%20with%20orange%20and%20golden%20hues%2C%20dynamic%20composition%2C%20no%20text&width=800&height=450&seq=variety-guide-article-11&orientation=landscape',
};

function getCategoryName(slug: string): string {
  const map: Record<string, string> = {
    recommend: '热门推荐',
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
    documentary: '纪录片',
  };
  return map[slug] || slug;
}

function getFallbackImage(articleId: string, category: string): string {
  if (articleCardImages[articleId]) return articleCardImages[articleId];
  return categoryImages[category] || categoryImages.recommend;
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === (slug || 'recommend'));

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  const categoryTitle = `${currentCategory.name} - 芒果视频`;
  const categoryDesc = `${currentCategory.description}。芒果视频作为蜜桃视频、糖豆视频之外的优质推荐，资源丰富播放流畅，免费高清值得选择。`;

  return (
    <>
      <SEOHead
        title={categoryTitle}
        description={categoryDesc}
        canonicalUrl={`https://www.mangoshipin.cn/category/${slug}`}
        ogTitle={categoryTitle}
        ogDescription={categoryDesc}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]}
        />

        <section className="w-full bg-background-50 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-14 md:py-20">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1 text-center lg:text-left">
                <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
                  {currentCategory.name}
                </h1>
                <p className="text-base md:text-lg text-foreground-500 max-w-lg">
                  {currentCategory.description}
                </p>
                <p className="text-sm text-foreground-400 mt-3">
                  共 {categoryArticles.length} 篇文章
                </p>
              </div>
              <div className="w-full lg:w-[400px] h-56 md:h-64 rounded-2xl overflow-hidden flex-shrink-0">
                <img
                  src={categoryImages[currentCategory.slug]}
                  alt={currentCategory.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 overflow-x-auto">
            <div className="flex flex-nowrap gap-2 min-w-max">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50 shadow-lg shadow-primary-500/25'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative w-full h-48 md:h-52 overflow-hidden">
                      <img
                        src={getFallbackImage(article.id, article.category)}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="text-base md:text-lg font-semibold font-heading text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}